import { useState } from "react";
import { Target, MessageCircle, Stethoscope, FileText, Lightbulb, Zap, ClipboardList, Network, LineChart, Hexagon, Construction } from "lucide-react";
import { ProblemsPanel } from "./ProblemsPanel";
import { DiagnosisPanel } from "./DiagnosisPanel";
import { JournalPanel } from "./JournalPanel";
import { BeckConceptualizationPanel } from "./BeckConceptualizationPanel";
import { DistortionsPanel } from "./DistortionsPanel";
import { ActConceptualizationPanel } from "./ActConceptualizationPanel";
import { StQuestionnairesPanel } from "./StQuestionnairesPanel";
import { StConceptualizationPanel } from "./StConceptualizationPanel";
import { StModesTrajectoryPanel } from "./StModesTrajectoryPanel";

const SECTIONS = [
  { group: null, items: [
    { id: "problems", label: "Définition du problème et des objectifs", icon: Target },
    { id: "anamnesis", label: "Entretien démographique et développemental", icon: MessageCircle },
    { id: "diagnosis", label: "Diagnostic DSM/CIM", icon: Stethoscope },
    { id: "journal", label: "Journal", icon: FileText },
  ] },
  { group: "TCC", items: [
    { id: "tcc_conceptualization", label: "Conceptualisation", icon: Lightbulb },
    { id: "tcc_distortions", label: "Distorsions cognitives", icon: Zap },
  ] },
  { group: "TS", items: [
    { id: "st_questionnaires", label: "Questionnaires ST", icon: ClipboardList },
    { id: "st_conceptualization", label: "Conceptualisation", icon: Network },
    { id: "st_modes", label: "Trajectoire des modes", icon: LineChart },
  ] },
  { group: "ACT", items: [{ id: "act_conceptualization", label: "Conceptualisation ACT", icon: Hexagon, test: true }] },
];
const TITLES = { problems: "Problèmes dominants et objectifs", diagnosis: "Diagnostic préliminaire", journal: "Journal du patient", tcc_conceptualization: "Conceptualisation cognitive (J. Beck)", tcc_distortions: "Distorsions cognitives", act_conceptualization: "Conceptualisation ACT", st_questionnaires: "Questionnaires de thérapie des schémas", st_conceptualization: "Conceptualisation en thérapie des schémas (Young)", st_modes: "Trajectoire des modes" };

export const ClinicalInfoWorkspace = ({ patientId, patientLang, sessions, onOpenAnamnesis }) => {
  const [section, setSection] = useState("problems");
  const pick = (id) => (id === "anamnesis" ? onOpenAnamnesis?.() : setSection(id));
  const current = SECTIONS.flatMap((g) => g.items).find((i) => i.id === section);
  return (
    <div className="grid xl:grid-cols-[280px_1fr] gap-6" data-testid="clinical-info-workspace">
      <aside className="rounded-xl border bg-card p-2 self-start xl:sticky xl:top-4 flex gap-1 overflow-x-auto scrollbar-none xl:block xl:overflow-visible" data-testid="clinical-sections">
        <p className="hidden xl:block px-3 py-2 text-xs font-semibold text-muted-foreground">Sections</p>
        {SECTIONS.map((g, gi) => (
          <div key={gi} className={`flex gap-1 xl:block ${gi > 0 ? "border-l pl-1 xl:border-l-0 xl:pl-0 xl:border-t xl:mt-1 xl:pt-1" : ""}`}>
            {g.group && <p className="hidden xl:block px-3 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{g.group}</p>}
            {g.items.map((it) => (
              <button key={it.id} onClick={() => pick(it.id)} className={`xl:w-full flex items-center gap-2 xl:gap-2.5 px-3 py-2 rounded-lg text-sm text-left whitespace-nowrap xl:whitespace-normal transition-colors ${section === it.id ? "bg-primary/10 text-primary font-medium xl:border-l-2 xl:border-primary" : "hover:bg-muted text-foreground"}`} data-testid={`clinical-section-${it.id}`}>
                <it.icon className="w-4 h-4 shrink-0" /> <span className="flex-1">{it.label}</span>
                {it.soon && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">Bientôt</span>}
                {it.test && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-semibold">EN TEST</span>}
              </button>
            ))}
          </div>
        ))}
      </aside>
      <section className="min-w-0 space-y-4">
        <h2 className="text-lg font-semibold flex items-center gap-2" data-testid="clinical-section-title">{current && <current.icon className="w-5 h-5 text-primary" />} {TITLES[section] || current?.label}</h2>
        {section === "problems" && <ProblemsPanel patientId={patientId} sessions={sessions} />}
        {section === "diagnosis" && <DiagnosisPanel patientId={patientId} />}
        {section === "journal" && <JournalPanel patientId={patientId} />}
        {section === "tcc_conceptualization" && <BeckConceptualizationPanel patientId={patientId} patientLang={patientLang} onOpenAnamnesis={onOpenAnamnesis} />}
        {section === "tcc_distortions" && <DistortionsPanel patientId={patientId} />}
        {section === "act_conceptualization" && <ActConceptualizationPanel patientId={patientId} patientLang={patientLang} />}
        {section === "st_questionnaires" && <StQuestionnairesPanel patientId={patientId} />}
        {section === "st_conceptualization" && <StConceptualizationPanel patientId={patientId} patientLang={patientLang} />}
        {section === "st_modes" && <StModesTrajectoryPanel patientId={patientId} patientLang={patientLang} />}
        {current?.soon && (
          <div className="rounded-xl border border-dashed p-8 text-center text-sm text-muted-foreground" data-testid="clinical-section-soon">
            <Construction className="w-6 h-6 mx-auto mb-2 text-amber-500" /> Cette section arrive dans la prochaine étape (spécifications reçues).
          </div>
        )}
      </section>
    </div>
  );
};
